import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import { Plus, FileText, Play, Eye, CheckCircle, Download, Trash2, AlertTriangle, Users, Clock, DollarSign } from 'lucide-react';

export default function PayrollBatchesPage() {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [processingBatch, setProcessingBatch] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [batchRes, periodRes] = await Promise.all([
        api.get('/payroll/batches'),
        api.get('/payroll/periods?status=OPEN')
      ]);
      setBatches(batchRes.data.items || []);
      setPeriods(periodRes.data.items || []);
    } catch (error) {
      notify.error(error, 'PAYROLL_BATCH_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!selectedPeriod) {
      notify.warn('PAYROLL_PERIOD_SELECT_REQUIRED');
      return;
    }
    try {
      await api.post('/payroll/batches', { period_id: selectedPeriod });
      toast.success('Payroll batch created');
      setDialogOpen(false);
      setSelectedPeriod('');
      fetchData();
    } catch (error) {
      notify.error(error, 'PAYROLL_BATCH_CREATE_FAILED');
    }
  };

  const handleAction = async (batchId, action, label) => {
    setProcessingBatch(batchId);
    try {
      await api.post(`/payroll/batches/${batchId}/${action}`);
      toast.success(`Batch ${label} successfully`);
      fetchData();
    } catch (error) {
      notify.error(error, 'PAYROLL_BATCH_ACTION_FAILED');
    } finally {
      setProcessingBatch(null);
    }
  };

  const handleExportCSV = async (batchId) => {
    setProcessingBatch(batchId);
    try {
      const response = await api.get(`/payroll/batches/${batchId}/export-csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payroll_${batchId}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Payroll exported successfully');
      fetchData();
    } catch (error) {
      notify.error(error, 'PAYROLL_BATCH_EXPORT_FAILED');
    } finally {
      setProcessingBatch(null);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'DRAFT':
        return <Badge variant="outline"><FileText className="h-3 w-3 mr-1" />Draft</Badge>;
      case 'REVIEW':
        return <Badge className="bg-yellow-500"><Eye className="h-3 w-3 mr-1" />Review</Badge>;
      case 'APPROVED':
        return <Badge className="bg-blue-500"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'EXPORTED':
        return <Badge className="bg-green-500"><Download className="h-3 w-3 mr-1" />Exported</Badge>;
      case 'VOID':
        return <Badge variant="destructive"><Trash2 className="h-3 w-3 mr-1" />Void</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getActionButtons = (batch) => {
    const isProcessing = processingBatch === batch.id;
    const buttons = [];

    if (batch.status === 'DRAFT') {
      buttons.push(
        <Button key="generate" size="sm" variant="outline" onClick={() => handleAction(batch.id, 'generate', 'generated')} disabled={isProcessing}>
          <Play className="h-3 w-3 mr-1" />Generate
        </Button>,
        <Button key="void" size="sm" variant="ghost" onClick={() => handleAction(batch.id, 'void', 'voided')} disabled={isProcessing}>
          <Trash2 className="h-3 w-3" />
        </Button>
      );
    } else if (batch.status === 'REVIEW') {
      buttons.push(
        <Button key="approve" size="sm" variant="outline" onClick={() => handleAction(batch.id, 'approve', 'approved')} disabled={isProcessing}>
          <CheckCircle className="h-3 w-3 mr-1" />Approve
        </Button>
      );
    } else if (batch.status === 'APPROVED') {
      buttons.push(
        <Button key="export" size="sm" onClick={() => handleExportCSV(batch.id)} disabled={isProcessing}>
          <Download className="h-3 w-3 mr-1" />Export
        </Button>
      );
    }

    // View details for any status
    buttons.push(
      <Button key="view" size="sm" variant="ghost" onClick={() => navigate(`/finance/payroll/${batch.id}`)}>
        <Eye className="h-3 w-3" />
      </Button>
    );

    return buttons;
  };

  if (loading) {
    return <div className="flex items-center justify-center p-8"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="space-y-6" data-testid="payroll-batches-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Payroll Batches</h1>
          <p className="text-muted-foreground">Process and export payroll for staff</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="create-batch-btn" disabled={periods.length === 0}>
              <Plus className="h-4 w-4 mr-2" />
              New Batch
            </Button>
          </DialogTrigger>
          <DialogContent>
            <form onSubmit={handleCreate}>
              <DialogHeader>
                <DialogTitle>Create Payroll Batch</DialogTitle>
                <DialogDescription>
                  Select an open payroll period to create a new batch.
                </DialogDescription>
              </DialogHeader>
              <div className="py-4">
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger data-testid="period-select">
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent>
                    {periods.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.period_no} ({p.start_date} to {p.end_date})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
                <Button type="submit" data-testid="save-batch-btn">Create Batch</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Batches</p>
                <p className="text-2xl font-bold">{batches.length}</p>
              </div>
              <FileText className="h-8 w-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Pending Review</p>
                <p className="text-2xl font-bold">{batches.filter(b => b.status === 'REVIEW').length}</p>
              </div>
              <Eye className="h-8 w-8 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Ready to Export</p>
                <p className="text-2xl font-bold">{batches.filter(b => b.status === 'APPROVED').length}</p>
              </div>
              <Download className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Exported</p>
                <p className="text-2xl font-bold">{batches.filter(b => b.status === 'EXPORTED').length}</p>
              </div>
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Batches</CardTitle>
          <CardDescription>Payroll batch processing workflow</CardDescription>
        </CardHeader>
        <CardContent>
          {batches.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-20" />
              <p>No payroll batches found</p>
              <p className="text-sm mt-1">Create a batch to start processing payroll</p>
            </div>
          ) : (
            <Table data-testid="batches-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Batch No</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Staff</TableHead>
                  <TableHead className="text-right">Hours</TableHead>
                  <TableHead className="text-right">Total Cost</TableHead>
                  <TableHead className="text-right">Exceptions</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {batches.map((batch) => (
                  <TableRow key={batch.id} data-testid={`batch-row-${batch.id}`}>
                    <TableCell className="font-medium">{batch.batch_no}</TableCell>
                    <TableCell>{batch.period_no || '-'}</TableCell>
                    <TableCell>{getStatusBadge(batch.status)}</TableCell>
                    <TableCell className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <Users className="h-3 w-3" />{batch.staff_count || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <Clock className="h-3 w-3" />{batch.total_hours || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className="flex items-center justify-end gap-1">
                        <DollarSign className="h-3 w-3" />{(batch.total_cost || 0).toLocaleString('en-AU', { minimumFractionDigits: 2 })}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      {(batch.exception_count || 0) > 0 ? (
                        <Badge variant="destructive" className="cursor-pointer" onClick={() => navigate(`/finance/payroll/${batch.id}/exceptions`)}>
                          <AlertTriangle className="h-3 w-3 mr-1" />{batch.exception_count}
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        {getActionButtons(batch)}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
